import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', role: 'piercer', country: 'HK', studio_ids: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [staffRes, studiosRes] = await Promise.all([
        api.get('/staff'),
        api.get('/studios')
      ]);
      setStaff(staffRes.data);
      setStudios(studiosRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', password: '', role: 'piercer', country: 'HK', studio_ids: [] });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (member) => {
    setForm({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      password: '',
      role: member.role,
      country: member.country,
      studio_ids: member.studios.map(s => s.studio_id)
    });
    setEditingId(member.id);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/staff/${editingId}`, payload);
        setSuccess('Staff member updated');
      } else {
        await api.post('/staff', form);
        setSuccess('Staff member created');
      }
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  const handleDeactivate = async (member) => {
    if (!window.confirm(`Deactivate ${member.name}? They will no longer be able to log in.`)) return;
    setError('');
    try {
      await api.delete(`/staff/${member.id}`);
      setSuccess(`${member.name} deactivated`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleStudioToggle = (studioId) => {
    setForm(prev => ({
      ...prev,
      studio_ids: prev.studio_ids.includes(studioId)
        ? prev.studio_ids.filter(id => id !== studioId)
        : [...prev.studio_ids, studioId]
    }));
  };

  const isMultiCountry = form.role === 'manager' || form.role === 'owner';
  const filteredStudios = isMultiCountry ? studios : studios.filter(s => s.country === form.country);

  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedStaff = [...staff].sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'studios') {
      aVal = a.studios.map(s => s.studio_name).join(', ');
      bVal = b.studios.map(s => s.studio_name).join(', ');
    } else if (sortField === 'status') {
      aVal = a.is_active ? 0 : 1;
      bVal = b.is_active ? 0 : 1;
    } else {
      aVal = (a[sortField] || '').toLowerCase();
      bVal = (b[sortField] || '').toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const sortThStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const sortIndicator = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : <span style={{ opacity: 0.3 }}> ▲</span>;

  if (loading) return <div className="page-container"><p>Loading...</p></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Staff Management</h1>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
            + New Staff Member
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="card form-card">
          <h2>{editingId ? 'Edit Staff Member' : 'New Staff Member'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text" required value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jane Smith"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email" required value={form.email || ''}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="e.g. jane@labret.co"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>WhatsApp Number <span style={{ fontWeight: 400, color: '#6b7280' }}>(for password reset)</span></label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+85291234567"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{editingId ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input
                  type="password"
                  required={!editingId}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 8 characters"
                  minLength={editingId && !form.password ? 0 : 8}
                />
              </div>
              {(!editingId || form.role !== 'owner') && (
                <div className="form-group">
                  <label>Role</label>
                  <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="piercer">Piercer</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Country</label>
                <select
                  value={form.country}
                  onChange={e => setForm({ ...form, country: e.target.value, studio_ids: [] })}
                  disabled={!!editingId}
                >
                  <option value="HK">Hong Kong</option>
                  <option value="SG">Singapore</option>
                  <option value="TH">Thailand</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assign to Studios</label>
                <div className="studio-checkboxes">
                  {filteredStudios.length === 0 && (
                    <span className="text-muted">No studios in this country yet</span>
                  )}
                  {filteredStudios.map(studio => (
                    <label key={studio.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.studio_ids.includes(studio.id)}
                        onChange={() => handleStudioToggle(studio.id)}
                      />
                      {studio.name}{isMultiCountry && <span style={{ marginLeft: 6, fontSize: '0.75rem', opacity: 0.6 }}>{studio.country}</span>}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Save Changes' : 'Create Staff Member'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{padding: 0}}>
        <div className="table-scroll-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={sortThStyle} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
              <th>Email</th>
              <th>WhatsApp</th>
              <th style={sortThStyle} onClick={() => handleSort('role')}>Role{sortIndicator('role')}</th>
              <th style={sortThStyle} onClick={() => handleSort('country')}>Country{sortIndicator('country')}</th>
              <th style={sortThStyle} onClick={() => handleSort('studios')}>Studios{sortIndicator('studios')}</th>
              <th style={sortThStyle} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedStaff.length === 0 && (
              <tr><td colSpan="8" style={{ textAlign: 'center' }}>No staff members found</td></tr>
            )}
            {sortedStaff.map(member => (
              <tr key={member.id} className={!member.is_active ? 'row-inactive' : ''}>
                <td><strong>{member.name}</strong></td>
                <td>{member.email}</td>
                <td>{member.phone || <span className="text-muted">Not set</span>}</td>
                <td>
                  <span className={`badge badge-${member.role}`}>
                    {member.role}
                  </span>
                </td>
                <td>{member.country}</td>
                <td>
                  {member.studios.length > 0
                    ? member.studios.map(s => s.studio_name).join(', ')
                    : <span className="text-muted">None assigned</span>
                  }
                </td>
                <td>
                  <span className={`badge ${member.is_active ? 'badge-active' : 'badge-inactive'}`}>
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(member)}>
                      Edit
                    </button>
                    {member.role !== 'owner' && member.is_active && (
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeactivate(member)}>
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}